import { useContext } from "react";

import { Context } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { WebPageURL, isoWebPageURL } from "../models/nominals";
import { ReactivatedContext, getReactivatedContext } from "./environment";

const enum NavContext {
    CURRENT,
    TOP,
}

type UnwrappedSiteURLs = Readonly<ReactivatedContext["urls"]>;
type SiteURLs = {
    [T in keyof UnwrappedSiteURLs]:
        | WebPageURL
        | Exclude<UnwrappedSiteURLs[T], string>;
};

const _getPageURLFromContext = <T extends keyof SiteURLs>(
    ctx: ReactivatedContext | null,
    name: T,
): SiteURLs[T] => {
    const siteURLs: UnwrappedSiteURLs | null = ctx?.urls || null;
    if (!siteURLs) {
        return isoWebPageURL.wrap("/");
    }
    const unwrappedURL = siteURLs[name];
    const url = unwrappedURL ? isoWebPageURL.wrap(unwrappedURL) : null;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return url!;
};

export const usePageURL = <T extends keyof SiteURLs>(name: T): SiteURLs[T] => {
    const ctx = useContext(Context) || getReactivatedContext();
    return _getPageURLFromContext(ctx, name);
};

/**
 * The Django ``STATIC_URL`` prefix from the reactivated context.
 *
 * Use this (plus a static-relative path) to build image ``src`` values in
 * server-rendered blocks instead of Vite asset ``import``s: Vite asset imports
 * resolve only against the client bundle and produce a broken URL during
 * reactivated SSR (silviogutierrez/reactivated#353). Because the project's
 * ``StaticFilesStorage`` disables per-file hashing, ``${STATIC_URL}${path}`` is
 * byte-identical to what the legacy ``{% static path %}`` tag emitted.
 * (ComparisonBlock / FinancingCardImage precedent.)
 */
export const useStaticURL = (): string => {
    const ctx = useContext(Context) || getReactivatedContext();
    return ctx?.STATIC_URL ?? "/static/";
};

export const useRequestPath = (): string => {
    const ctx = useContext(Context) || getReactivatedContext();
    return ctx?.request?.path ?? "/";
};

export const navigateToURL = (
    url: WebPageURL,
    context = NavContext.TOP,
): void => {
    const window = getWindow();
    if (!window) return;

    const unwrappedURL = isoWebPageURL.unwrap(url);
    if (context === NavContext.TOP && window.top) {
        window.top.location.href = unwrappedURL;
        return;
    }
    window.location.href = unwrappedURL;
};

export const navigateToHome = (): void => {
    const window = getWindow();
    if (!window) return;

    const homeURL = isoWebPageURL.wrap(window.location.origin);
    navigateToURL(homeURL);
};

export const getHash = (elem: string): boolean => {
    const window = getWindow();
    if (!window) return false;

    const hasPDPHash =
        window.location.hash.length > 0 && window.location.hash.includes(elem);
    if (hasPDPHash) {
        return true;
    }
    return false;
};
